import React, { useState, useEffect } from 'react';
import { X, Package2, Plus, Building2 } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';
import { getSuppliers, createSupplier, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';

interface EditStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: StockItem | null;
}

export default function EditStockModal({
  isOpen,
  onClose,
  onSuccess,
  item
}: EditStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    unit: '',
    price: '',
    acquisition_cost: '',
    quantity: '',
    reorder_point: '',
    max_quantity: '',
    is_sellable: false
  });

  useEffect(() => {
    if (isOpen && item) {
      setIsAnimating(true);
      setFormData({
        name: item.name,
        description: item.description || '',
        sku: item.sku || '',
        unit: item.unit,
        price: item.price.toString(),
        acquisition_cost: item.acquisition_cost.toString(),
        quantity: item.quantity.toString(),
        reorder_point: item.reorder_point.toString(),
        max_quantity: item.max_quantity.toString(),
        is_sellable: item.is_sellable || false
      });
      setSelectedSupplier(item.supplier_id);
      
      // Load suppliers when modal opens
      if (selectedBusiness?.id) {
        loadSuppliers();
      }
    }
  }, [isOpen, item, selectedBusiness?.id]);

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleAddSupplier = async () => {
    if (!selectedBusiness?.id) return;
    
    if (!supplierFormData.first_name || !supplierFormData.email) {
      setError('Supplier first name and email are required');
      return;
    }
    
    try {
      setLoading(true);
      
      const supplier = await createSupplier({
        business_id: selectedBusiness.id,
        type: 'person',
        first_name: supplierFormData.first_name,
        last_name: supplierFormData.last_name,
        email: supplierFormData.email,
        phone: supplierFormData.phone || null,
        address: null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false
      });
      
      await loadSuppliers();
      setSelectedSupplier(supplier.id);
      setIsAddingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;

    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      const maxQuantity = parseInt(formData.max_quantity);
      const reorderPoint = parseInt(formData.reorder_point);
      
      if (maxQuantity <= reorderPoint) {
        throw new Error('Maximum quantity must be greater than reorder point');
      }

      if (!supabase) {
        throw new Error('Database connection not available');
      }

      // Update stock item
      const { error: updateError } = await supabase
        .from('stock_items')
        .update({
          name: formData.name,
          description: formData.description || null,
          sku: formData.sku || null,
          unit: formData.unit,
          price: parseFloat(formData.price),
          acquisition_cost: parseFloat(formData.acquisition_cost),
          quantity: parseInt(formData.quantity),
          reorder_point: parseInt(formData.reorder_point),
          max_quantity: maxQuantity,
          is_sellable: formData.is_sellable,
          supplier_id: selectedSupplier
        })
        .eq('id', item.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating stock item:', err);
      setError(err instanceof Error ? err.message : 'Failed to update stock item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!item) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[9999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Edit Stock Item</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SKU
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Retail Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acquisition Cost
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.acquisition_cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, acquisition_cost: e.target.value }))}
                      className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reorder Point
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.reorder_point}
                    onChange={(e) => setFormData(prev => ({ ...prev, reorder_point: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.max_quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_quantity: e.target.value }))}
                    className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_sellable}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_sellable: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-sm text-gray-700">Sellable Item</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Make this item available for sale to customers
                </p>
              </div>

              {/* Supplier Selection */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Supplier</h3>
                
                {isAddingSupplier ? (
                  <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={supplierFormData.first_name}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={supplierFormData.last_name}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter last name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={supplierFormData.email}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={supplierFormData.phone}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter phone number (optional)"
                      />
                    </div>

                    <div className="flex justify-end space-x-2 mt-4">
                      <button
                        type="button"
                        onClick={() => setIsAddingSupplier(false)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSupplier}
                        disabled={loading}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                      >
                        {loading ? 'Adding...' : 'Add Supplier'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {suppliers.length > 0 ? (
                      <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => setSelectedSupplier(null)}
                          className={`w-full flex items-center p-3 rounded-lg border ${
                            selectedSupplier === null
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-sm text-left">No supplier</span>
                        </button>
                        
                        {suppliers.map((supplier) => (
                          <button
                            key={supplier.id}
                            type="button"
                            onClick={() => setSelectedSupplier(supplier.id)}
                            className={`w-full flex items-center p-3 rounded-lg border ${
                              selectedSupplier === supplier.id
                                ? 'border-gray-900 bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                              <Building2 className="h-4 w-4 text-gray-400" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="text-sm font-medium text-gray-900">
                                {supplier.first_name} {supplier.last_name}
                              </div>
                              {supplier.email && (
                                <div className="text-xs text-gray-500">{supplier.email}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 border border-gray-200 rounded-lg">
                        <Building2 className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No suppliers available</p>
                      </div>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => setIsAddingSupplier(true)}
                      className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Supplier
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}